import { XMarkIcon } from '@heroicons/react/24/outline';
import styles from './PersonModal.module.css';

/**
 * Modal for assigning/editing player information.
 *
 * Single responsibility: Display player form with suggestions.
 * Save logic is passed via onSave prop.
 */
export default function PersonModal({
  isOpen,
  position,
  personForm,
  personSuggestions,
  highConfNeighbors,
  selectedFace,
  savingPerson,
  // Team search props
  teamSearchQuery,
  teamSuggestions,
  selectedTeam,
  onTeamSearchChange,
  onSelectTeam,
  onClearTeam,
  // Form handlers
  onFormChange,
  onSelectSuggestion,
  onSelectNeighbor,
  onSave,
  onClose,
  onDragStart,
}) {
  if (!isOpen) return null;

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div
        className={styles.card}
        onClick={(e) => e.stopPropagation()}
        style={{ transform: `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px))` }}
      >
        <div className={styles.header} onMouseDown={onDragStart}>
          <h4>Assign player</h4>
          <button className={styles.closeBtn} onClick={onClose}>
            <XMarkIcon className={styles.icon} />
          </button>
        </div>

        <div className={styles.body}>
          <PersonForm
            personForm={personForm}
            teamSearchQuery={teamSearchQuery}
            teamSuggestions={teamSuggestions}
            selectedTeam={selectedTeam}
            onFormChange={onFormChange}
            onTeamSearchChange={onTeamSearchChange}
            onSelectTeam={onSelectTeam}
            onClearTeam={onClearTeam}
          />

          {personSuggestions.length > 0 && (
            <SuggestionsList
              suggestions={personSuggestions}
              onSelect={onSelectSuggestion}
            />
          )}

          {selectedFace && (
            <SimilarFaces
              neighbors={highConfNeighbors}
              onPick={onSelectNeighbor}
            />
          )}
        </div>

        <div className={styles.footer}>
          <button className={styles.cancelBtn} onClick={onClose}>
            Cancel
          </button>
          <button
            className={styles.saveBtn}
            disabled={savingPerson}
            onClick={onSave}
          >
            {savingPerson ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Form fields for person data.
 */
function PersonForm({
  personForm,
  teamSearchQuery,
  teamSuggestions,
  selectedTeam,
  onFormChange,
  onTeamSearchChange,
  onSelectTeam,
  onClearTeam,
}) {
  const handleChange = (field) => (e) => {
    onFormChange({ ...personForm, [field]: e.target.value });
  };

  return (
    <div className={styles.formGrid}>
      <div className={styles.formField}>
        <label>First name</label>
        <input
          placeholder="First name"
          value={personForm.first_name}
          autoComplete="off"
          autoFocus
          onChange={handleChange('first_name')}
        />
      </div>
      <div className={styles.formField}>
        <label>Last name</label>
        <input
          placeholder="Last name"
          value={personForm.last_name}
          autoComplete="off"
          onChange={handleChange('last_name')}
        />
      </div>
      <div className={styles.formField}>
        <label>Team</label>
        <TeamSearchInput
          query={teamSearchQuery}
          suggestions={teamSuggestions}
          selectedTeam={selectedTeam}
          onQueryChange={onTeamSearchChange}
          onSelect={onSelectTeam}
          onClear={onClearTeam}
        />
      </div>
      <div className={`${styles.formField} ${styles.formFieldHalf}`}>
        <label>Jersey #</label>
        <input
          placeholder="#"
          value={personForm.jersey_number}
          inputMode="numeric"
          autoComplete="off"
          onChange={handleChange('jersey_number')}
        />
      </div>
      <div className={`${styles.formField} ${styles.formFieldHalf}`}>
        <label>Prefix</label>
        <input
          placeholder="Dr., etc."
          value={personForm.prefix}
          autoComplete="off"
          onChange={handleChange('prefix')}
        />
      </div>
      <div className={`${styles.formField} ${styles.formFieldHalf}`}>
        <label>Suffix</label>
        <input
          placeholder="Jr., Sr."
          value={personForm.suffix}
          autoComplete="off"
          onChange={handleChange('suffix')}
        />
      </div>
    </div>
  );
}

/**
 * Searchable team input with autocomplete suggestions.
 */
function TeamSearchInput({ query, suggestions, selectedTeam, onQueryChange, onSelect, onClear }) {
  return (
    <div className={styles.teamSearchContainer}>
      <div className={styles.teamInputWrapper}>
        <input
          type="text"
          className={styles.teamSearchInput}
          placeholder="Search teams..."
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          autoComplete="off"
        />
        {selectedTeam && (
          <button
            type="button"
            className={styles.clearTeamBtn}
            onClick={onClear}
            title="Clear team"
          >
            <XMarkIcon className={styles.clearIcon} />
          </button>
        )}
      </div>
      {suggestions.length > 0 && (
        <div className={styles.teamSuggestionsList}>
          {suggestions.map((team) => (
            <button
              key={team.id}
              type="button"
              className={styles.teamSuggestionItem}
              onClick={() => onSelect(team)}
            >
              <span className={styles.teamName}>{team.name}</span>
              {team.team_type && (
                <span className={styles.teamType}>{team.team_type}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * List of matching player suggestions.
 */
function SuggestionsList({ suggestions, onSelect }) {
  return (
    <div className={styles.suggestionsContainer}>
      <div className={styles.suggestionsHeader}>
        <span className={styles.suggestionsLabel}>Matching players</span>
        <span className={styles.suggestionsCount}>
          {suggestions.length > 10 ? '10+' : suggestions.length}
        </span>
      </div>
      <div className={styles.suggestionsList}>
        {suggestions.slice(0, 10).map((p) => (
          <button
            key={p.id}
            type="button"
            className={styles.suggestionItem}
            onClick={() => onSelect(p)}
          >
            <span className={styles.suggestionName}>
              {p.display_name || `${p.first_name || ''} ${p.last_name || ''}`.trim()}
            </span>
            {p.jersey_number && (
              <span className={styles.suggestionJersey}>#{p.jersey_number}</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Clickable list of similar persons derived from high-confidence face
 * neighbors (>80% similarity). Each row is a single Person — neighbors
 * are deduped by person_id keeping the closest match. Clicking a row
 * assigns the active face to that person directly.
 *
 * Replaces the prior thumbnail grid, which relied on face thumbnails
 * that Matrix doesn't generate.
 */
function SimilarFaces({ neighbors, onPick }) {
  const rows = (() => {
    const byPerson = new Map();
    for (const n of neighbors || []) {
      const pid = n.person_id || n.person?.id || null;
      if (!pid) continue;
      const name =
        n.person_name ||
        n.person_display ||
        n.person?.display_name ||
        '';
      if (!name) continue;
      const distance = typeof n.distance === 'number' ? n.distance : null;
      const prior = byPerson.get(pid);
      if (!prior || (distance != null && distance < prior.distance)) {
        byPerson.set(pid, { person_id: pid, name, distance });
      }
    }
    return Array.from(byPerson.values()).sort((a, b) => {
      const da = a.distance == null ? Infinity : a.distance;
      const db = b.distance == null ? Infinity : b.distance;
      return da - db;
    });
  })();

  if (rows.length === 0) return null;

  const fmtPct = (d) => {
    if (d == null) return '—';
    const pct = Math.max(0, Math.min(100, (1 - d) * 100));
    return `${Math.round(pct)}%`;
  };

  return (
    <div className={styles.suggestionsContainer} style={{ marginTop: 14 }}>
      <div className={styles.suggestionsHeader}>
        <span className={styles.suggestionsLabel}>Similar persons (&gt;80%)</span>
        <span className={styles.suggestionsCount}>{rows.length}</span>
      </div>
      <div className={styles.suggestionsList}>
        {rows.slice(0, 6).map((r) => (
          <button
            key={r.person_id}
            type="button"
            className={styles.suggestionItem}
            onClick={() => onPick?.(r)}
            disabled={!onPick}
            title="Click to assign this person to the active face"
          >
            <span className={styles.suggestionName}>{r.name}</span>
            <span className={styles.suggestionJersey}>{fmtPct(r.distance)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
